import { differenceInYears, isFuture, isValid, parse } from 'date-fns';

import { MAX_DISALLOWED_BIRTH_YEAR } from '@/constants';
import { UpdateAccountInput } from '@/types';

import { getPhoneValidatorRules, toE164Phone } from '../Input/phoneValidator';

const EMPTY_ADDRESS_FIELDS = {
  address1: '',
  address2: '',
  city: '',
  country: '',
  country_code: '',
  latitude: undefined as number | undefined,
  longitude: undefined as number | undefined,
  province: '',
  province_code: '',
  zip: '',
};

const getAccountValidationRules = (minAge: number) => [
  {
    field: 'first_name',
    message: 'First name is required.',
    method: 'isEmpty',
    validWhen: false,
  },
  {
    field: 'last_name',
    message: 'Last name is required.',
    method: 'isEmpty',
    validWhen: false,
  },
  {
    field: 'email',
    message: 'Enter your email.',
    method: 'isEmpty',
    validWhen: false,
  },
  {
    field: 'email',
    message: 'The email format is incorrect.',
    method: 'isEmail',
    validWhen: true,
  },
  ...getPhoneValidatorRules({ field: 'phone', acceptE164: true }),
  {
    field: 'date_of_birth',
    message: 'The birthdate format is incorrect.',
    method: 'isDate',
    validWhen: true,
  },
  {
    field: 'date_of_birth',
    message: 'Enter your date of birth.',
    method: 'isEmpty',
    validWhen: false,
  },
  {
    field: 'date_of_birth',
    message: `Should be at least ${minAge} years old.`,
    method: current => {
      return differenceInYears(new Date(), new Date(current)) >= minAge;
    },
    validWhen: true,
  },
  {
    field: 'driver_license_id_expiration_date',
    message: 'Driver License should not be expired.',
    method: current => {
      if (isValid(parse(current, 'P', new Date()))) {
        return isFuture(new Date(current));
      }

      return true;
    },
    validWhen: true,
  },
  {
    field: 'date_of_birth',
    message: `Birth year must be ${MAX_DISALLOWED_BIRTH_YEAR + 1} or later.`,
    method: current => {
      if (!current) {
        return true;
      }

      const parsedDate = parse(current, 'P', new Date());
      if (!isValid(parsedDate)) {
        return true;
      }

      return parsedDate.getFullYear() > MAX_DISALLOWED_BIRTH_YEAR;
    },
    validWhen: true,
  },
  {
    field: 'medical_id_expiration_date',
    message: 'Medical ID should not be expired.',
    method: current => {
      if (isValid(parse(current, 'P', new Date()))) {
        return isFuture(new Date(current));
      }

      return true;
    },
    validWhen: true,
  },
  {
    args: [{ min: 5 }],
    field: 'medical_id',
    message: 'Write at least 5 characters.',
    method: 'isLength',
    validWhen: true,
  },
];

const getPasswordValidationRules = (passwordInputs: { password?: string }) => [
  {
    args: [{ min: 8 }],
    field: 'oldpassword',
    message: 'Write at least 8 characters.',
    method: 'isLength',
    validWhen: true,
  },
  {
    args: [{ min: 8 }],
    field: 'password',
    message: 'Write at least 8 characters.',
    method: 'isLength',
    validWhen: true,
  },
  {
    args: [passwordInputs.password],
    field: 'passwordmatch',
    message: "Passwords doesn't match.",
    method: 'equals',
    validWhen: true,
  },
];

/**
 * Clears the single account address from state and form inputs.
 * Use when the user removes their only address.
 */
const clearAccountAddress = ({
  inputs,
  setInputs,
  setState,
  state,
}: {
  inputs: Record<string, unknown>;
  setInputs: (_value: Record<string, unknown>) => void;
  setState: (_patch: Record<string, unknown>) => void;
  state: unknown;
}) => {
  const s = state as Record<string, unknown>;
  setState({
    ...s,
    addresses: [],
    delivery_address: {
      ...(s.delivery_address as Record<string, unknown>),
      ...EMPTY_ADDRESS_FIELDS,
    },
  });
  setInputs({
    ...inputs,
    ...EMPTY_ADDRESS_FIELDS,
  });
};

const formatDate = (date?: string) => {
  if (!date) return '';
  const [year, month, day] = date?.split('T')[0].split('-') || [];
  return `${month}/${day}/${year}`;
};

type MedicalIdFieldError = {
  isInvalid: boolean;
  message: string;
};

const isMedicalIdPairInvalid = (inputs: Record<string, unknown>) => {
  const hasNumber = Boolean(inputs?.medical_id?.toString().trim());
  const hasExp = Boolean(inputs?.medical_id_expiration_date?.toString().trim());

  return (hasExp && !hasNumber) || (hasNumber && !hasExp);
};

const getMedicalIdPairErrors = (
  inputs: Record<string, unknown>,
): {
  medical_id?: MedicalIdFieldError;
  medical_id_expiration_date?: MedicalIdFieldError;
} => {
  const hasNumber = Boolean(inputs?.medical_id?.toString().trim());
  const hasExp = Boolean(inputs?.medical_id_expiration_date?.toString().trim());

  if (hasExp && !hasNumber) {
    return {
      medical_id: {
        isInvalid: true,
        message: 'Medical ID number is required.',
      },
    };
  }

  if (hasNumber && !hasExp) {
    return {
      medical_id_expiration_date: {
        isInvalid: true,
        message: 'Medical ID expiration date is required.',
      },
    };
  }

  return {};
};

const mapUpdateAccountInput = (inputs): UpdateAccountInput => {
  const phoneNumber = toE164Phone(inputs?.phone) ?? undefined;

  const input: UpdateAccountInput = {
    // PII
    email: inputs.email,
    first_name: inputs.first_name,
    last_name: inputs.last_name,
    phone: phoneNumber,
    date_of_birth: new Date(inputs.date_of_birth).toISOString(),

    photo: inputs.photo,

    gender: inputs.gender,

    // Preferences
    accept_marketing: inputs.accept_marketing,

    // address
    address1: inputs.address1,
    address2: inputs.address2,
    city: inputs.city,
    city_code: inputs.city_code,
    country: inputs.country,
    country_code: inputs.country_code,
    province: inputs.province,
    province_code: inputs.province_code,
    latitude: inputs.latitude,
    longitude: inputs.longitude,
    zip: inputs.zip,

    // DL
    driver_license_id: inputs.driver_license_id,
    driver_license_photo: inputs.driver_license_photo,

    type: inputs.type,

    // Other
    tags: inputs?.tags,
  };

  const medicalNumber =
    (typeof inputs?.medical_id === 'string' && inputs.medical_id.trim()) ||
    null;
  const hasMedicalNumber = !!medicalNumber;
  const medicalExp =
    typeof inputs?.medical_id_expiration_date === 'string'
      ? inputs.medical_id_expiration_date.trim()
      : '';
  const hasMedicalExp = Boolean(medicalExp);
  const hasMedicalPhoto = Boolean(inputs?.medical_id_photo);

  // medical_id requires BOTH number and expiration_date together (backend rejects a
  // partial). The form blocks submit when only one is filled (isMedicalIdPairInvalid),
  // so here we just send the pair when both exist, and omit medical_id otherwise.
  if (hasMedicalNumber && hasMedicalExp) {
    input.medical_id = {
      number: medicalNumber,
      expiration_date: new Date(medicalExp).toISOString(),
    };
  }

  if (hasMedicalPhoto) {
    input.medical_document_photo_uri = inputs.medical_id_photo;
  }

  if (
    inputs.driver_license_id_expiration_date &&
    !inputs.driver_license_id_expiration_date?.includes('undefined') // our date input default the value to "undefined/undefined/undefined"
  ) {
    input.driver_license_id_expiration_date = new Date(
      inputs.driver_license_id_expiration_date,
    ).toISOString();
  } else {
    input.driver_license_id_expiration_date = undefined;
  }

  return input;
};

export {
  clearAccountAddress,
  formatDate,
  getAccountValidationRules,
  getMedicalIdPairErrors,
  getPasswordValidationRules,
  isMedicalIdPairInvalid,
  mapUpdateAccountInput,
};
